/* eslint-disable @typescript-eslint/indent */
import {
  Appointment,
  AppointmentProduct,
  AppointmentType,
  Clinic,
  CreateAppointmentResponseData,
  graphql,
  isAppointment,
  isAppointmentType,
  Membership,
  PatientMembership,
  RescheduleAppointmentResponseData,
  StripeTokenResponse,
  TimeSlot,
  utils,
  ValidPromo,
  ReduxState
} from 'kb-shared';
import { ApolloError } from 'apollo-boost';
import gql from 'graphql-tag';
import Honeybadger from '@honeybadger-io/js';
import React from 'react';
import { Mutation, QueryResult } from 'react-apollo';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';

import { showErrorToast } from '../../../utilities/notificationUtils';
import CancellationPolicyModal from '../components/CancellationPolicyModal';
import ConfirmDetails from '../components/ConfirmDetails';
import PaymentForm from '../components/PaymentForm';
import MembershipDetails from './MembershipDetails';
import PaymentDetails, { ProductType } from './PaymentDetails/PaymentDetails';
import { PATIENT_MEMBERSHIP_QUERY } from './SelectOption';
import { PaymentFormWrapper } from './styles';
import StripeWrapper from 'hoc/StripeWrapper';
import PatientStripeProvider from 'hoc/PatientStripeProvider';

import { connect } from 'react-redux';

export type CreateAppointmentVariables = {
  timeSlotId: number;
  appointmentTypeId: number;
  // Only need stripe if you are purchasing an appointment
  stripeTokenId: number;
  // Only need these if you are booking a package appointment
  patientPackageId: number;
  packageItemId: number;
};

export const FRAGMENT_APPOINTMENT_FIELDS = gql`
  fragment AppointmentFields on Appointment {
    id
    videoLink
    location {
      id
      address
      name
      patientFacing
      timeZone
      lab {
        id
      }
    }
    startTime
    endTime
    timeZone
    cancelable
    reschedulable
    appointmentType {
      id
      virtual
      name
      description
      videoLink
      rateCents
      category {
        id
        name
      }
    }
  }
`;

export const CREATE_APPOINTMENT = gql`
  mutation createAppointment(
    $timeSlotId: Int!
    $appointmentTypeId: Int!
    $stripeTokenId: String
    $patientPackageId: Int
    $packageItemId: Int
    $discountCode: String
  ) {
    createAppointment(
      timeSlotId: $timeSlotId
      appointmentTypeId: $appointmentTypeId
      stripeTokenId: $stripeTokenId
      patientPackageId: $patientPackageId
      packageItemId: $packageItemId
      discountCode: $discountCode
    ) {
      appointment {
        ...AppointmentFields
      }
    }
  }
  ${FRAGMENT_APPOINTMENT_FIELDS}
`;

export const CREATE_MEMBERSHIP = gql`
  mutation createPatientMembership(
    $membershipId: Int!
    $stripeTokenId: String
    $discountCode: String
  ) {
    createPatientMembership(
      membershipId: $membershipId
      stripeTokenId: $stripeTokenId
      discountCode: $discountCode
    ) {
      membership {
        id
        name
        rateCents
      }
    }
  }
`;

export const RESCHEDULE_APPOINTMENT = gql`
  mutation rescheduleAppointment($appointmentId: Int!, $newTimeSlotId: Int!) {
    rescheduleAppointment(appointmentId: $appointmentId, newTimeSlotId: $newTimeSlotId) {
      appointment {
        ...AppointmentFields
      }
    }
  }
  ${FRAGMENT_APPOINTMENT_FIELDS}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  padding-right: 20px;
  margin: 0 14px;
`;

const DisclaimerText = styled.div`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    max-width: 340px;
  }
  margin-bottom: 16px;
  background-color: ${({ theme }) => theme.colors.KBBeige};
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.KBNight};
  padding: 10px;
`;

interface Props extends RouteComponentProps<{}> {
  product: AppointmentProduct<AppointmentType> | null;
  timeSlot: TimeSlot | null;
  clinic: Clinic | null; // Optional for virtual appointments
  hasEmployer: boolean;
  onBookAppointment: (appointment: Appointment) => void;
  onPurchaseMembership: (membership: Membership) => void;
  hasMembershipEmployer: boolean;
}

type State = {
  cardholderName: string;
  loading: boolean;
  promoCode: string;
  validPromo: ValidPromo | null;
  promoError: string | null;
  paymentTermsChecked: boolean;
  showPaymentModal: boolean;
  showCancellationPolicy: boolean;
};

interface AppointmentData {
  appointment: Appointment;
}

class ConfirmOrder extends React.Component<Props, State> {
  state: State = {
    cardholderName: '',
    loading: false,
    promoCode: '',
    validPromo: null,
    promoError: null,
    paymentTermsChecked: false,
    showPaymentModal: false,
    showCancellationPolicy: false
  };

  membershipPromoCode: string | null = null;

  componentDidMount() {
    const { product, history } = this.props;
    if (!product) {
      history.replace('/');
    }
  }

  purchaseAppointmentSucceeded({ appointment }: AppointmentData) {
    this.setState(() => ({ loading: false }));
    this.props.onBookAppointment(appointment);
  }

  // @ts-ignore
  purchaseMembershipSucceeded = res => {
    this.setState(() => ({ loading: false }));
    // @ts-ignore
    this.props.onPurchaseMembership(res.data.createPatientMembership);
  };

  showCancellationPolicy = () => {
    this.setState({ showCancellationPolicy: true });
  };

  hideCancellationPolicy = () => {
    this.setState({ showCancellationPolicy: false });
  };

  renderConnectedForm() {
    const { timeSlot, product, clinic, hasEmployer } = this.props;
    const { paymentTermsChecked, loading, validPromo } = this.state;

    if (!product) return;

    const handleError = (err: ApolloError | null) => {
      let errString = "We're sorry, something went wrong. Please try again.";
      if (err) {
        const message = err.message.toLowerCase();
        if (message.includes('location')) {
          errString = `Looks like you're trying to book an appointment in a new city! Please reach out to ${utils.KBContacts.navigatorEmail} so we can activate your new account.`;
        } else if (message.includes('an error occurred while processing your card')) {
          errString =
            'An error occurred while processing your card. Please try again in a little bit.';
        } else if (message.includes('expired')) {
          errString = 'Your card has expired.';
        } else if (message.includes('security code')) {
          errString = "Your card's security code is incorrect.";
        } else if (message.includes('your card was declined')) {
          errString = 'Your card was declined.';
        } else if (message.includes('insufficient funds')) {
          errString = 'Your card has insufficient funds.';
        } else if (message.includes('time slot has already been taken')) {
          errString = 'This appointment time has already been taken.';
        } else {
          Honeybadger.notify(err, 'UnhandledPaymentError');
        }
      } else {
        // Entering an invalid CC, CVC or expiration number results in err being undefined.
        errString =
          'Please verify that you have correctly entered your credit card number, expiration date and security code.';
      }

      if (window.analytics) {
        window.analytics.track('Appointment Booking Failed', { error: errString });
      }

      this.setState(() => ({ loading: false }));
      showErrorToast(errString);
    };

    if (product && product.type === 'appointment' && timeSlot) {
      const appointmentType = product.data;
      return (
        <StripeWrapper stripeKey={timeSlot.lab?.stripePublicKey}>
          <Mutation<
            CreateAppointmentResponseData,
            {
              timeSlotId: number;
              appointmentTypeId: number;
              stripeTokenId?: string | null;
              discountCode?: string | null;
            }
          >
            mutation={CREATE_APPOINTMENT}
            refetchQueries={[{ query: graphql.query.PATIENT_APPOINTMENTS }]}
          >
            {createAppointment => {
              return (
                <PaymentForm
                  appointment={appointmentType}
                  clinic={clinic}
                  product={product}
                  timeSlot={timeSlot}
                  paymentTermsChecked={paymentTermsChecked}
                  loading={loading}
                  onPaymentTermsChecked={checked => {
                    this.setState({ paymentTermsChecked: checked });
                  }}
                  onValidPromoChange={validPromo => {
                    this.setState({ validPromo });
                  }}
                  validPromo={validPromo}
                  onSubmit={(token: StripeTokenResponse) => {
                    this.setState({ loading: true });
                    const tokenId = token.token && token.token.id;
                    if (tokenId) {
                      createAppointment({
                        variables: {
                          timeSlotId: parseInt(timeSlot.id, 10),
                          appointmentTypeId: parseInt(appointmentType.id, 10),
                          stripeTokenId: tokenId,
                          discountCode:
                            this.state.validPromo &&
                            // @ts-ignore
                            this.state.validPromo.code
                        }
                      })
                        // @ts-ignore
                        .then(response => {
                          // @ts-ignore
                          this.purchaseAppointmentSucceeded(response.data?.createAppointment);
                        })
                        .catch(handleError);
                    } else {
                      // If for any reason stripe fails in an unexpected way
                      // @ts-ignore
                      handleError();
                    }
                  }}
                  noPayment={
                    appointmentType.rateCents === 0
                      ? () => {
                          createAppointment({
                            variables: {
                              timeSlotId: parseInt(timeSlot.id, 10),
                              appointmentTypeId: parseInt(appointmentType.id, 10)
                            }
                          })
                            // @ts-ignore
                            .then(response => {
                              // @ts-ignore
                              this.purchaseAppointmentSucceeded(response.data.createAppointment);
                            })
                            .catch(handleError);
                        }
                      : undefined
                  }
                />
              );
            }}
          </Mutation>
        </StripeWrapper>
      );
    } else if (product && product.type === 'membership') {
      const appointmentType = product.data;
      return (
        // @ts-ignore
        <PatientStripeProvider>
          <Mutation<
            QueryResult<PatientMembership>,
            {
              membershipId: number;
              stripeTokenId: string;
              dsicountCode: string;
            }
          >
            mutation={CREATE_MEMBERSHIP} // Refetch the current patient, which updates their membership status
            refetchQueries={[{ query: PATIENT_MEMBERSHIP_QUERY }]}
          >
            {createPatientMembership => {
              return (
                <PaymentForm
                  appointment={appointmentType}
                  clinic={clinic}
                  product={product}
                  timeSlot={timeSlot}
                  paymentTermsChecked={paymentTermsChecked}
                  loading={loading}
                  hasEmployer={hasEmployer}
                  onPaymentTermsChecked={checked => {
                    this.setState({ paymentTermsChecked: checked });
                  }}
                  onValidPromoChange={validPromo => {
                    this.setState({ validPromo });
                  }}
                  validPromo={validPromo}
                  onSubmit={(token: StripeTokenResponse) => {
                    this.setState({ loading: true });
                    const tokenId = token.token && token.token.id;
                    const membership: Membership = product.data;
                    if (tokenId) {
                      createPatientMembership({
                        variables: {
                          membershipId: parseInt(membership.id),
                          stripeTokenId: tokenId,
                          // @ts-ignore
                          discountCode: this.state.validPromo && this.state.validPromo.code
                        }
                      })
                        // @ts-ignore
                        .then(this.purchaseMembershipSucceeded)
                        .catch(handleError);
                    } else {
                      // If for any reason stripe fails in an unexpected way
                      // @ts-ignore
                      handleError();
                    }
                  }}
                  noPayment={
                    hasEmployer
                      ? () => {
                          createPatientMembership({
                            variables: {
                              membershipId: parseInt(product.data.id),
                              // @ts-ignore
                              discountCode: this.state.validPromo && this.state.validPromo.code
                            }
                          })
                            // @ts-ignore
                            .then(this.purchaseMembershipSucceeded)
                            .catch(handleError);
                        }
                      : undefined
                  }
                />
              );
            }}
          </Mutation>
        </PatientStripeProvider>
      );
    } else if (product && product.type === 'reschedule_appointment' && timeSlot) {
      return (
        <Mutation<
          RescheduleAppointmentResponseData,
          { appointmentId: number; newTimeSlotId: number }
        >
          mutation={RESCHEDULE_APPOINTMENT}
          refetchQueries={[{ query: graphql.query.PATIENT_APPOINTMENTS }]}
        >
          {rescheduleAppointment => {
            // Grab a reference to the appointment that is being rescheduled.
            // @ts-ignore
            const appointment: Appointment = product.data;
            return this.renderRescheduleForm(() => {
              rescheduleAppointment({
                variables: {
                  appointmentId: parseInt(appointment.id, 10),
                  newTimeSlotId: parseInt(timeSlot.id, 10)
                }
              })
                // @ts-ignore
                .then(response => {
                  // @ts-ignore
                  this.purchaseAppointmentSucceeded(response.data.rescheduleAppointment);
                })
                .catch(handleError);
            });
          }}
        </Mutation>
      );
    } else {
      if (Honeybadger) {
        Honeybadger.notify(
          `wrong conditions in renderConnectedForm, type: ${product.type}, timeslot: ${timeSlot &&
            timeSlot.id}`
        );
      }
      return <div>Something went wrong. Please try again.</div>;
    }
  }

  renderRescheduleForm(onSubmit: () => void) {
    const { timeSlot, product, clinic } = this.props;
    const { paymentTermsChecked, loading, validPromo } = this.state;
    if (!product) {
      return null;
    }
    const appointment = product.data;
    if (!isAppointment(appointment) && !isAppointmentType(appointment)) {
      return null;
    }

    return (
      <PaymentFormWrapper>
        <form
          onSubmit={async e => {
            e.preventDefault();
            this.setState(() => ({ loading: true }));
            onSubmit();
          }}
        >
          <ConfirmDetails
            clinic={clinic}
            appointment={appointment}
            product={product}
            timeSlot={timeSlot}
            loading={loading}
            paymentTermsChecked={paymentTermsChecked}
            validPromo={validPromo}
            onPaymentTermsChecked={checked => {
              this.setState({ paymentTermsChecked: checked });
            }}
            onValidPromoChange={validPromo => {
              this.setState({ validPromo });
            }}
          />
        </form>
      </PaymentFormWrapper>
    );
  }

  renderPaymentDetails() {
    const { product } = this.props;
    // @ts-ignore
    if (!product || !product.data || product.data.rateCents === 0) return null;

    let productType: ProductType = 'REGULAR_APPOINTMENT';

    if (product.type === 'appointment') {
      const categoryId = product.data.category.id;
      const requiresImmediatePayment = utils.requiresPaymentAtBooking(
        parseInt(categoryId),
        process.env.REACT_APP_ENV
      );
      const isGYNAppointment = utils.isGYNAppointment(
        parseInt(categoryId),
        process.env.REACT_APP_ENV
      );

      if (requiresImmediatePayment) {
        productType = 'REQUIRES_IMMEDIATE_PAYMENT';
      } else if (isGYNAppointment) {
        productType = 'GYN_APPOINTMENT';
      }
    } else if (product.type === 'reschedule_appointment') {
      productType = 'RESCHEDULE_APPOINTMENT';
    }

    return (
      <PaymentDetails
        productType={productType}
        showCancellationPolicy={this.showCancellationPolicy}
      />
    );
  }

  render() {
    const { hasEmployer, hasMembershipEmployer, product } = this.props;

    const categoryName = product?.type == 'appointment' ? product?.data.category.name : undefined;

    return (
      <Container>
        {hasMembershipEmployer && categoryName == 'Coaching' && (
          <DisclaimerText>
            If you have employer benefits with us, please call us at 855-KND-BODY or email
            navigator@kindbody.com to book your appointment and apply any applicable benefit
            discounts.
          </DisclaimerText>
        )}
        {hasEmployer && <MembershipDetails />}
        {!hasEmployer && this.renderPaymentDetails()}
        {this.renderConnectedForm()}
        <CancellationPolicyModal
          show={this.state.showCancellationPolicy}
          onClickClose={this.hideCancellationPolicy}
        />
      </Container>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return { hasMembershipEmployer: state.patient.patient.patientMembership?.employer ?? false };
}

export default withRouter(connect(mapStateToProps)(ConfirmOrder));
